/**
 * HH-6: display vocabulary for the Weekritme — labels, icon names and
 * date/time formatting only.
 *
 * There is deliberately NO occurrence computation here. Which items fall
 * on which day (weekday bitmask, season bounds, skip exceptions) is
 * answered once, server-side, by ETM_Schedule_Logic — the same restraint
 * WP-2 documented for isDeadlineAtRisk() and the engine's slack math: a
 * second implementation in JS is a second implementation to disagree.
 * This file only speaks about what the server already decided.
 *
 * The one piece of the backend's model that does live here is the weekday
 * bit table, because the form has to send a mask. It is Monday-first
 * (bit 0 = Monday … bit 6 = Sunday) exactly like ETM_Schedule_Logic, and
 * it is written out rather than computed from Date#getDay(), which is
 * Sunday-first and would silently shift every item by a day.
 */
import { __ } from '../i18n.js';

export const WEEKDAYS = [
  { bit: 1, short: () => __('ma', 'etm'), long: () => __('maandag', 'etm') },
  { bit: 2, short: () => __('di', 'etm'), long: () => __('dinsdag', 'etm') },
  { bit: 4, short: () => __('wo', 'etm'), long: () => __('woensdag', 'etm') },
  { bit: 8, short: () => __('do', 'etm'), long: () => __('donderdag', 'etm') },
  { bit: 16, short: () => __('vr', 'etm'), long: () => __('vrijdag', 'etm') },
  { bit: 32, short: () => __('za', 'etm'), long: () => __('zaterdag', 'etm') },
  { bit: 64, short: () => __('zo', 'etm'), long: () => __('zondag', 'etm') },
];

export const ALL_WEEKDAYS = 127;
const WEEKDAYS_MASK = 31; // ma–vr
const WEEKEND_MASK = 96; // za+zo

/** Is this weekday bit set in the mask? */
export function hasWeekday(mask, bit) {
  return (Number(mask) & bit) !== 0;
}

/** Flips one weekday bit, returning the new mask. */
export function toggleWeekday(mask, bit) {
  return hasWeekday(mask, bit) ? Number(mask) & ~bit : Number(mask) | bit;
}

/**
 * Human summary of a weekday mask: the three common shapes get their own
 * word ("Elke dag", "Doordeweeks", "Weekend"), everything else lists the
 * short day names in week order.
 */
export function weekdaysSummary(mask) {
  const value = Number(mask) || 0;
  if (value === 0) return '';
  if (value === ALL_WEEKDAYS) return __('Elke dag', 'etm');
  if (value === WEEKDAYS_MASK) return __('Doordeweeks', 'etm');
  if (value === WEEKEND_MASK) return __('Weekend', 'etm');

  return WEEKDAYS.filter((day) => hasWeekday(value, day.bit))
    .map((day) => day.short())
    .join(', ');
}

/**
 * 'Y-m-d' -> a Date pinned to UTC midnight.
 *
 * The timeZone: 'UTC' on every formatter below exists for the same
 * reason: without it a browser west of UTC renders 2026-08-17T00:00:00Z
 * as 16 August, so the day heading would disagree with the date the
 * server put the occurrence on. The dates in this module are calendar
 * dates, not instants.
 */
function toUtcDate(ymd) {
  if (!ymd) return null;
  const date = new Date(`${ymd}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** 'maandag 17 augustus' — the day-list heading. */
export function formatDayHeading(ymd) {
  const date = toUtcDate(ymd);
  if (!date) return '';
  return date.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

/** 'ma' / '17' — the compact desktop column header. */
export function formatDayShort(ymd) {
  const date = toUtcDate(ymd);
  if (!date) return { weekday: '', day: '' };
  return {
    weekday: date.toLocaleDateString('nl-NL', { weekday: 'short', timeZone: 'UTC' }),
    day: date.toLocaleDateString('nl-NL', { day: 'numeric', timeZone: 'UTC' }),
  };
}

/** '17 – 23 aug' — the week navigator's label. */
export function formatWeekRange(startYmd, endYmd) {
  const start = toUtcDate(startYmd);
  const end = toUtcDate(endYmd);
  if (!start || !end) return '';

  const endLabel = end.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const startLabel = sameMonth
    ? start.toLocaleDateString('nl-NL', { day: 'numeric', timeZone: 'UTC' })
    : start.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'UTC' });

  return `${startLabel} – ${endLabel}`;
}

/**
 * '18:30 – 19:30', '18:30', or null when the item has no fixed moment.
 * Null is the caller's cue to show the "hele dag" label instead — an
 * item without a time is a real state, not missing data.
 */
export function formatOccurrenceTime(occurrence) {
  if (!occurrence || !occurrence.time_start) return null;
  return occurrence.time_end ? `${occurrence.time_start} – ${occurrence.time_end}` : occurrence.time_start;
}

/**
 * Curated icon set for schedule items, same shape and reasoning as
 * CATEGORY_ICON_CHOICES (WP-1): a picker small enough to stay one tap,
 * covering what a household's week actually contains.
 */
export const SCHEDULE_ICON_CHOICES = [
  'school',
  'sports_soccer',
  'music_note',
  'medical_services',
  'directions_bike',
  'pool',
  'fitness_center',
  'restaurant',
  'menu_book',
  'directions_car',
  'group',
  'event',
];

/** Neutral fallback for empty/unknown stored icon values. */
export const SCHEDULE_FALLBACK_ICON = 'event';

export function scheduleIconName(stored) {
  return SCHEDULE_ICON_CHOICES.includes(stored) ? stored : SCHEDULE_FALLBACK_ICON;
}

/**
 * Advance-warning presets. `warn_minutes_before` is stored and editable
 * from HH-6 on, but nothing consumes it yet — the digest section that
 * reads it is HH-7's job, and the form says so in its own hint rather
 * than implying a reminder that would not arrive (the HH-2 precedent for
 * `warn_days_before`).
 */
export const WARN_OPTIONS = [
  { value: '', label: () => __('Geen', 'etm') },
  { value: 15, label: () => __('15 min', 'etm') },
  { value: 60, label: () => __('1 uur', 'etm') },
  { value: 1440, label: () => __('1 dag', 'etm') },
];
